import { useEffect, useId, useRef, useState } from "react";
import {
  IonCheckbox,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonToggle,
  type CheckboxCustomEvent,
} from "@ionic/react";
import type { SubModel } from "model";
import { Alert, Badge, Card, Colors, Icon } from "swing-components";

import { Loader } from "~components/Atoms/Loader";
import { translate, type PreferredSubRequest } from "~utils";

import { ModalHeader } from "../ModalHeader";
import { SelectItem } from "../SelectItem";
import { useSubs } from "./useSubs";

/***** Types *****/
type ChooseSubFieldProps = {
  request: PreferredSubRequest;
  selectedSubs: SubModel[];
  isCredentialRequired: boolean;
  isCredentialedPositionSelected: boolean | undefined;
  hideCredentialToggle?: boolean;
  onConfirm: (v: { selectedSubs: SubModel[]; isCredentialRequired: boolean }) => void;
};

/***** Component *****/
export function ChooseSubField(props: ChooseSubFieldProps) {
  const {
    request,
    selectedSubs,
    isCredentialRequired,
    isCredentialedPositionSelected,
    onConfirm,
    hideCredentialToggle,
  } = props;

  /*** Hooks ***/
  const modalId = useId();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [allowCredOnly, setAllowCredOnly] = useState(
    isCredentialedPositionSelected ?? isCredentialRequired,
  );
  const [tempSelectedSubs, setTempSelectedSubs] = useState<SubModel[]>(selectedSubs);

  const { isError, subs, isLoading } = useSubs({ request, query: searchTerm });

  /*** Constants ***/
  // Deduplicate subs by ID using a Map, then convert to Set
  const allSubs = new Set(
    Array.from(
      new Map(
        [...filterSubsByName({ subs, searchTerm }), ...tempSelectedSubs].map((sub) => [
          sub.id,
          sub,
        ]),
      ).values(),
    ),
  );
  // Arrays holding subs for each category of the list
  const internalSubs = new Set([...allSubs].filter((sub) => sub.isInternal));
  const favoriteSubs = new Set([...allSubs].filter((sub) => sub.isSchoolFavorited)).difference(
    internalSubs,
  );
  const remainingSubs = allSubs.difference(internalSubs.union(favoriteSubs));

  /*** Effects ***/
  /** Update the selected subs when the prop changes. */
  useEffect(() => {
    const derivedCredValue = isCredentialedPositionSelected ?? isCredentialRequired;
    setTempSelectedSubs(selectedSubs);
    setAllowCredOnly(derivedCredValue);
  }, [isCredentialRequired, selectedSubs, isCredentialedPositionSelected]);

  /*** Handlers ***/
  function onModalDismiss() {
    setTempSelectedSubs(selectedSubs);
    setAllowCredOnly(isCredentialedPositionSelected ?? isCredentialRequired);
    setSearchTerm("");
    modalRef.current?.dismiss();
  }

  function onModalConfirm() {
    onConfirm({
      selectedSubs: tempSelectedSubs,
      isCredentialRequired: allowCredOnly,
    });
    modalRef.current?.dismiss();
  }

  function handleSubChange(e: CheckboxCustomEvent) {
    const { checked, value } = e.detail;
    if (checked) {
      setTempSelectedSubs((subs) => [...subs, value]);
    } else {
      setTempSelectedSubs(tempSelectedSubs.filter((item) => item.id !== value.id));
    }
  }

  function isChecked(sub: SubModel) {
    return tempSelectedSubs.find((item) => item.id === sub.id) !== undefined;
  }

  /*** Render ***/
  return (
    <SelectItem
      id={modalId}
      label={translate("SUB_SELECT_HEADER")}
      value={translate("SUB_SELECT_DISPLAY_VALUE", {
        count: selectedSubs.length,
      })}
      stacked
    >
      <IonModal trigger={modalId} ref={modalRef} backdropDismiss onDidDismiss={onModalDismiss}>
        <ModalHeader
          title={translate("SUB_SELECT_HEADER")}
          onModalConfirm={onModalConfirm}
          onModalDismiss={onModalDismiss}
          confirmButtonText={translate("LABEL_DONE")}
          isConfirmTextDisabled={isError}
          onIonInput={(e) => setSearchTerm(e?.target?.value || "")}
        />

        {/* Modal Content */}
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            padding: 16,
            overflow: "auto",
            height: "100%",
            backgroundColor: Colors.white300,
          }}
        >
          {/* Credentialed Position Alert */}
          {isCredentialedPositionSelected && (
            <Alert css={{ margin: 0 }} color="info">
              {translate("SUB_SELECT_ALERT_CREDENTIALED")}
            </Alert>
          )}

          {/* Credential Toggle */}
          {!hideCredentialToggle && (
            <IonList lines="none" css={{ padding: 0 }}>
              <IonItem>
                <IonLabel className="font-medium">
                  {translate("SUB_SELECT_LABEL_ALLOW_CRED_ONLY")}
                </IonLabel>
                <IonToggle
                  aria-label="credential-permit-toggle"
                  slot="end"
                  onIonChange={(e: CustomEvent) => {
                    setAllowCredOnly(e.detail.checked);

                    if (e.detail.checked) {
                      const credentialedSubs = tempSelectedSubs.filter((sub) => sub.isCredentialed);
                      setTempSelectedSubs(credentialedSubs);
                    }
                  }}
                  checked={allowCredOnly}
                />
              </IonItem>
            </IonList>
          )}

          {/* Loading or Error State */}
          {isError ? (
            <div className="flex justify-center mt-4">
              <span>{translate("GENERAL_ERROR")}</span>
            </div>
          ) : isLoading ? (
            <Loader />
          ) : (
            <>
              {/* Internal Subs - Only show if there are any */}
              {internalSubs.size > 0 && (
                <Section
                  header={translate("SUB_SELECT_LABEL_INTERNAL_SECTION")}
                  data={[...internalSubs]}
                  isChecked={isChecked}
                  onChange={handleSubChange}
                  allowCredOnly={allowCredOnly}
                  testId="internal-subs-header"
                />
              )}

              {/* Favorite Subs - Always show, displays "None" if
              empty */}
              <Section
                header={translate("SUB_SELECT_LABEL_FAVORITES_SECTION")}
                data={[...favoriteSubs]}
                isChecked={isChecked}
                onChange={handleSubChange}
                allowCredOnly={allowCredOnly}
                testId="favorite-subs-header"
              />

              {/* All Subs - Always show, displays "None" if empty */}
              <Section
                header={translate("SUB_SELECT_LABEL_ALL_SECTION")}
                data={[...remainingSubs]}
                isChecked={isChecked}
                onChange={handleSubChange}
                allowCredOnly={allowCredOnly}
                testId="all-subs-header"
              />
            </>
          )}
        </div>
      </IonModal>
    </SelectItem>
  );
}

/***** Helper Components *****/
type SectionProps = {
  header: string;
  data: SubModel[];
  isChecked: (sub: SubModel) => boolean;
  onChange: (e: CheckboxCustomEvent) => void;
  allowCredOnly: boolean;
  testId?: string;
};

/**
 * Section component that renders a card with a header and list of
 * subs. If data is empty, displays "None" in italic.
 */
function Section(props: SectionProps) {
  const { header, data, isChecked, onChange, allowCredOnly, testId } = props;

  return (
    <Card>
      {/* Section Header */}
      <div
        css={{
          position: "sticky",
          top: -16,
          paddingTop: 16,
          marginTop: -16,
          backgroundColor: Colors.white200,
          zIndex: 5,
        }}
      >
        <div
          css={{
            backgroundColor: Colors.slate200,
            padding: 16,
            borderRadius: 8,
          }}
        >
          <p
            css={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
            }}
            data-testid={testId}
          >
            {header}
          </p>
        </div>
      </div>

      {/* Section List */}
      <div>
        {data.length === 0 ? (
          <div
            css={{
              padding: 16,
              fontStyle: "italic",
            }}
          >
            {translate("LABEL_NONE")}
          </div>
        ) : (
          data.map((sub) => (
            <SubCheckboxRow
              key={sub.id}
              sub={sub}
              isChecked={isChecked(sub)}
              onChange={onChange}
              allowCredOnly={allowCredOnly}
            />
          ))
        )}
      </div>
    </Card>
  );
}

function SubCheckboxRow({
  sub,
  isChecked,
  onChange,
  allowCredOnly,
}: {
  sub: SubModel;
  isChecked: boolean;
  onChange: (e: CheckboxCustomEvent) => void;
  allowCredOnly: boolean;
}) {
  const isDisabled = !isSubSelectable({
    sub,
    isCredentialRequired: allowCredOnly,
  });

  return (
    <IonCheckbox
      key={sub.id}
      labelPlacement="end"
      justify="start"
      value={sub}
      checked={isChecked}
      onIonChange={onChange}
      disabled={isDisabled}
      css={{
        width: "100%",
        paddingTop: 16,
        display: "flex",
        justifyContent: "space-between",
        paddingBottom: 16,
        boxShadow: `inset 0 -1px 0 ${Colors.slate200}`,
        // Styling the label to wrap to two lines and ellipsis
        // after the second line
        // See https://ionicframework.com/docs/api/checkbox
        "&::part(label)": {
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          display: "-webkit-box",
          whiteSpace: "normal",
        },
        ":last-of-type": {
          paddingBottom: 0,
          boxShadow: "none",
        },
      }}
    >
      <span>{sub.name}</span>
      {/* Badge or Credential Icon */}
      {!sub.isEligible ? (
        <Badge status="light" text={translate("SUB_SELECT_LABEL_UNAVAILABLE")} />
      ) : sub.isCredentialed ? (
        <div slot="end">
          <Icon name="Verified" color={Colors.green500} />
        </div>
      ) : (
        <Badge status="light" text={translate("SUB_SELECT_LABEL_NON_CRED")} />
      )}
    </IonCheckbox>
  );
}

/***** Helper Functions *****/
/**
 * Determines if a sub should be selectable based on eligibility and
 * credential requirements.
 * Returns true if the sub is available/selectable, false if it should
 * be disabled.
 */
export function isSubSelectable(params: { sub: SubModel; isCredentialRequired: boolean }): boolean {
  const { sub, isCredentialRequired } = params;

  return sub.isEligible && (!isCredentialRequired || sub.isCredentialed);
}

/**
 * Given a list of Subs, filter out any subs where their name does not include
 * the search string.
 */
function filterSubsByName(params: { subs: SubModel[]; searchTerm: string }) {
  const { subs, searchTerm } = params;

  return subs.filter((sub) => sub.name.toLowerCase().includes(searchTerm.toLowerCase()));
}
